import { useMemo } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface LoadPoint {
  date: string;
  ctl: number;
  atl: number;
  tsb: number;
}

interface CombinedLoadChartProps {
  data: LoadPoint[];
  freshnessStatus?: string;
}

const FRESHNESS_COLORS: Record<string, { text: string; bg: string }> = {
  fresh: { text: "text-emerald-400", bg: "bg-emerald-500/10" },
  neutral: { text: "text-blue-400", bg: "bg-blue-500/10" },
  fatigued: { text: "text-amber-400", bg: "bg-amber-500/10" },
  overreached: { text: "text-red-400", bg: "bg-red-500/10" },
};

export function CombinedLoadChart({ data, freshnessStatus }: CombinedLoadChartProps) {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    return data.map((p) => ({
      date: p.date,
      ctl: Math.round(p.ctl * 10) / 10,
      atl: Math.round(p.atl * 10) / 10,
      tsb: Math.round(p.tsb * 10) / 10,
    }));
  }, [data]);

  if (!chartData.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">Combined Cross-Sport Load</h3>
        <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">
          No combined load data available. Train across multiple sports to see your combined fatigue model.
        </div>
      </div>
    );
  }

  const fStyles = freshnessStatus ? FRESHNESS_COLORS[freshnessStatus] ?? FRESHNESS_COLORS.neutral : null;

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Combined Cross-Sport Load</h3>
        {freshnessStatus && fStyles && (
          <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${fStyles.bg} ${fStyles.text}`}>
            {freshnessStatus}
          </span>
        )}
      </div>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="combinedTsbGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(170 80% 45%)" stopOpacity={0.15} />
                <stop offset="95%" stopColor="hsl(170 80% 45%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={40} />
            <Tooltip
              contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }}
            />
            <Area type="monotone" dataKey="tsb" stroke="hsl(170 80% 45%)" fill="url(#combinedTsbGrad)" strokeWidth={1} name="TSB" />
            <Line type="monotone" dataKey="ctl" stroke="hsl(200 65% 55%)" strokeWidth={1.5} dot={false} name="CTL" />
            <Line type="monotone" dataKey="atl" stroke="hsl(0 70% 55%)" strokeWidth={1.5} dot={false} name="ATL" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
